/** «Финансы»: shapes of GET /finance/payments and GET /finance/payments/{id}
 *  (src/api/admin/finance.py, src/core/finance/service.py) and the pure logic of the page —
 *  the list query and the texts of a fee rule and a timeline event. No I/O. */

import { gatewayFits } from '@/lib/paymentLabels'

export interface PaymentRow {
  id: string
  user_id: number
  provider: string
  method: string
  /** the effective gateway (an СБП row without one is PayGateCore's) */
  gateway: string
  channel: string
  channel_label: string
  provider_payment_id: string | null
  product_type: string
  product_ref: string | null
  promo_code: string | null
  amount: string
  currency: string
  credits_to_grant: string
  status: string
  rate_rub: string | null
  /** RUB snapshot at paid time (docs/accounting.md §Комиссии); null until paid */
  amount_rub: string | null
  fee_rub: string | null
  net_rub: string | null
  net_amount: string | null
  net_currency: string | null
  created_at: string | null
  paid_at: string | null
  expires_at: string | null
  refunded_at: string | null
  /** the card only (GET /finance/payments/{id}): what was returned and what still can be */
  refunded_amount?: string
  refundable_amount?: string
  credits_clawed_back?: string
  credits_left_to_claw?: string
  /** false for Telegram Stars: refundStarPayment returns the whole charge only */
  partial_allowed?: boolean
}

/** One refund of the payment (payment_refunds): partial or the final one. */
export interface PaymentRefundRow {
  id: number
  amount: string
  currency: string
  amount_rub: string | null
  credits: string
  is_final: boolean
  /** admin | provider | legacy */
  source: string
  reason: string | null
  admin_id: number | null
  created_at: string | null
}

export interface FeeRuleRef {
  id: number
  provider: string
  kind: string
  pct: string
  fixed: string
  fixed_currency: string | null
  effective_from: string
  is_estimate: boolean
}

export interface FeeItem {
  id: number
  kind: string
  /** provider_actual | provider_quote | rule_estimate */
  source: string
  amount: string
  currency: string
  rate_rub: string | null
  amount_rub: string | null
  rule_id: number | null
  rule: FeeRuleRef | null
  note: string | null
  created_at: string | null
  /** the row that counts for its kind (payment_fees is append-only: a quote stays next to the
   *  actual figure that replaced it) */
  winning: boolean
}

export interface TimelineItem {
  at: string
  /** created | expires | expired | fee | paid | refunded | admin */
  event: string
  detail: string
}

/** One provider event of the payment (webhook_log.events_for_payment — no payload). */
export interface PaymentEvent {
  id: number
  received_at: string | null
  source: string
  provider: string
  gateway: string | null
  event_status: string | null
  outcome: string
  signature_ok: boolean | null
  error: string | null
  duration_ms: number | null
}

export interface PaymentDetail {
  payment: PaymentRow
  user: { id: number; tg_id: number | null; username: string | null }
  fees: FeeItem[]
  timeline: TimelineItem[]
  webhooks: PaymentEvent[]
  refunds?: PaymentRefundRow[]
}

export const PAYMENT_STATUSES = [
  'created',
  'pending',
  'paid',
  'partially_paid',
  'partially_refunded',
  'expired',
  'failed',
  'refunded',
] as const

/** Statuses a refund can still start from (in part or for the rest). */
export const REFUNDABLE_STATUSES = ['paid', 'partially_refunded'] as const

export function isRefundable(status: string): boolean {
  return (REFUNDABLE_STATUSES as readonly string[]).includes(status)
}

export interface FinanceFilters {
  method: string
  gateway: string
  status: string
  /** users.id */
  user: string
  /** our payment id, the provider's payment id or the customer's Telegram id */
  q: string
  dateField: 'created' | 'paid'
  from: string
  to: string
}

export const EMPTY_FINANCE_FILTERS: FinanceFilters = {
  method: '',
  gateway: '',
  status: '',
  user: '',
  q: '',
  dateField: 'created',
  from: '',
  to: '',
}

export type FinanceQuery = { ok: true; path: string } | { ok: false; errors: { user?: string } }

/** The list request, or the error that stops it. A gateway that does not belong to the chosen
 *  method is dropped rather than sent (the answer would always be empty). */
export function financeListQuery(f: FinanceFilters, offset: number, limit: number): FinanceQuery {
  const user = f.user.replace(/\s/g, '')
  if (user && !/^\d{1,18}$/.test(user)) {
    return { ok: false, errors: { user: 'Нужен числовой id пользователя; Telegram id ищите в поле «Поиск»' } }
  }
  const qs = new URLSearchParams()
  qs.set('limit', String(limit))
  qs.set('offset', String(offset))
  if (f.method) qs.set('method', f.method)
  if (f.gateway && gatewayFits(f.method, f.gateway)) qs.set('gateway', f.gateway)
  if (f.status) qs.set('status', f.status)
  if (user) qs.set('user_id', user)
  const q = f.q.trim()
  if (q) qs.set('q', q)
  if (f.dateField === 'paid') qs.set('date_field', 'paid')
  if (f.from) qs.set('date_from', f.from)
  if (f.to) qs.set('date_to', f.to)
  return { ok: true, path: `/finance/payments?${qs.toString()}` }
}

/** A decimal string for reading: digits grouped, the fraction padded to `min` digits and its
 *  trailing zeros dropped down to `min`. Exact — a non-zero digit is never cut or rounded
 *  (a display must not change a money value), and nothing becomes a float. */
function scaledText(s: string | null | undefined, min: number): string {
  if (s === null || s === undefined || s === '') return '—'
  const m = /^(-?)(\d+)(?:\.(\d+))?$/.exec(s)
  if (!m) return s
  const whole = (m[2] ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
  let frac = (m[3] ?? '').replace(/0+$/, '')
  if (frac.length < min) frac = frac.padEnd(min, '0')
  return `${m[1]}${whole}${frac ? `,${frac}` : ''}`
}

/** "1.5000" → "1,5"; "0.300000" → "0,3" — a rate or a rule figure, trailing zeros dropped. */
export function decText(s: string | null | undefined): string {
  return scaledText(s, 0)
}

/** A decimal with at least `min` fraction digits, digits grouped, exact:
 *  ("5.0", 1) → "5,0", ("12.25", 1) → "12,25", ("3", 2) → "3,00"; null → "—". */
export function decimalText(s: string | null | undefined, min: number): string {
  return scaledText(s, min)
}

/** Decimals a unit is always shown with. The API sends the column scale (payments.amount is
 *  NUMERIC(18,6): "1490.000000"), so zeros beyond it are dropped down to this: RUB and USD
 *  keep kopecks / cents, USDT drops its zeros (up to the 6 of the column), Stars are whole.
 *  A unit not listed here only loses its trailing zeros. */
const UNIT_DECIMALS: Record<string, number> = { RUB: 2, USD: 2, EUR: 2, USDT: 0, XTR: 0 }

/** A money figure at its unit's natural scale, digits grouped — exact, no float:
 *  ("1490.000000", "RUB") → "1 490,00", ("149.000000", "USDT") → "149",
 *  ("15.200000", "USDT") → "15,2". Without a unit it is a ruble (or percent) figure. */
export function moneyText(s: string | null | undefined, unit: string | null = 'RUB'): string {
  return scaledText(s, UNIT_DECIMALS[unit ?? ''] ?? 0)
}

/** Credits: whole, or with the (at most 2) decimals they really have —
 *  "500.000000" → "500", "12.250000" → "12,25". */
export function creditsText(s: string | null | undefined): string {
  return scaledText(s, 0)
}

/** A payment amount with its currency: "1 490,00 RUB", "149 USDT", "1 500 XTR". */
export function amountText(p: { amount: string | null; currency: string | null }): string {
  if (p.amount === null || p.amount === '') return '—'
  return `${moneyText(p.amount, p.currency)} ${p.currency ?? ''}`.trim()
}

/** A fee rule in words: «2 % + 0,3 USDT с 01.09.2026, оценка». */
export function ruleText(rule: FeeRuleRef): string {
  const parts: string[] = []
  const pct = decText(rule.pct)
  const fixed = decText(rule.fixed)
  if (pct !== '0') parts.push(`${pct} %`)
  if (fixed !== '0') parts.push(`${fixed} ${rule.fixed_currency ?? ''}`.trim())
  const value = parts.length > 0 ? parts.join(' + ') : '0 %'
  const [y, m, d] = rule.effective_from.split('-')
  const since = y && m && d ? `${d}.${m}.${y}` : rule.effective_from
  return `${value} с ${since}${rule.is_estimate ? ', оценка' : ''}`
}

export const TIMELINE_LABEL: Record<string, string> = {
  created: 'Счёт создан',
  expires: 'Оплатить до',
  expired: 'Срок истёк',
  fee: 'Комиссия',
  paid: 'Оплачен',
  refunded: 'Возврат',
  partial_refund: 'Частичный возврат',
  admin: 'Действие админа',
}

export function timelineLabel(event: string): string {
  return TIMELINE_LABEL[event] ?? event
}

export const PRODUCT_LABEL: Record<string, string> = { credits: 'Пакет кредитов', subscription: 'Подписка' }

/** The same filters for the total (GET /finance/payments/totals) or the CSV
 *  (GET /finance/payments.csv): the list's path without its page. */
export function financeAggregatePath(listPath: string, kind: 'totals' | 'csv'): string {
  const [, qs = ''] = listPath.split('?')
  const params = new URLSearchParams(qs)
  params.delete('limit')
  params.delete('offset')
  const rest = params.toString()
  const base = kind === 'totals' ? '/finance/payments/totals' : '/finance/payments.csv'
  return rest ? `${base}?${rest}` : base
}

/** GET /finance/payments/totals */
export interface PaymentsTotals {
  count: number
  by_currency: { currency: string; count: number; amount: string }[]
  amount_rub: string
  without_rub: number
}

/** Compare two non-negative decimal strings exactly (no float): -1, 0, 1. */
function cmpDecimal(a: string, b: string): number {
  const [ai = '0', af = ''] = a.split('.')
  const [bi = '0', bf = ''] = b.split('.')
  const ia = ai.replace(/^0+(?=\d)/, '')
  const ib = bi.replace(/^0+(?=\d)/, '')
  if (ia.length !== ib.length) return ia.length < ib.length ? -1 : 1
  if (ia !== ib) return ia < ib ? -1 : 1
  const len = Math.max(af.length, bf.length)
  const fa = af.padEnd(len, '0')
  const fb = bf.padEnd(len, '0')
  return fa === fb ? 0 : fa < fb ? -1 : 1
}

/**
 * MONEY: the ⚡ to take back on a refund (POST /finance/payments/{id}/refund `amount`): a
 * decimal from 0 to what the payment granted. The payment itself is refunded in full; this is
 * only how much of its ⚡ leaves the balance (less when the user already spent them — the rest
 * becomes debt otherwise). Empty = all of them (the server's default). Returns the amount to
 * send (null = default) or an error.
 */
export function refundClawback(raw: string, granted: string): { ok: true; amount: string | null } | { ok: false; error: string } {
  const v = raw.trim().replace(',', '.')
  if (v === '') return { ok: true, amount: null }
  if (!/^\d{1,12}(\.\d{1,6})?$/.test(v)) return { ok: false, error: 'Нужно число от 0, не больше 6 знаков после запятой' }
  if (cmpDecimal(v, granted.replace(/^-/, '')) > 0) return { ok: false, error: 'Больше, чем осталось списать' }
  return { ok: true, amount: v }
}

// ── partial refunds (docs/accounting.md «Частичные возвраты») ──

const SCALE = 6 // NUMERIC(18,6) money, NUMERIC(20,6) ⚡

function toScaled(s: string): bigint | null {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(s.trim())
  if (!m) return null
  const frac = (m[2] ?? '').padEnd(SCALE, '0').slice(0, SCALE)
  return BigInt(`${m[1] ?? '0'}${frac}`)
}

function fromScaled(v: bigint): string {
  const digits = v.toString().padStart(SCALE + 1, '0')
  const whole = digits.slice(0, digits.length - SCALE)
  const frac = digits.slice(digits.length - SCALE).replace(/0+$/, '')
  return frac ? `${whole}.${frac}` : whole
}

/**
 * MONEY: the money to return (POST …/refund `refund_amount`, the payment's currency). Empty =
 * everything still refundable (the final refund, the server's default). A part must be
 * positive, at most 6 decimals and not more than what is left; Telegram Stars refund only whole.
 */
export function refundMoney(
  raw: string,
  refundable: string,
  partialAllowed: boolean,
): { ok: true; amount: string | null; final: boolean } | { ok: false; error: string } {
  const v = raw.trim().replace(/\s/g, '').replace(',', '.')
  if (v === '') return { ok: true, amount: null, final: true }
  if (!/^\d{1,12}(\.\d{1,6})?$/.test(v)) return { ok: false, error: 'Нужно число больше 0, не больше 6 знаков после запятой' }
  const x = toScaled(v) ?? 0n
  const left = toScaled(refundable) ?? 0n
  if (x <= 0n) return { ok: false, error: 'Сумма возврата должна быть больше 0' }
  if (x > left) return { ok: false, error: 'Больше, чем осталось вернуть' }
  if (x < left && !partialAllowed) return { ok: false, error: 'Telegram Stars возвращаются только целиком' }
  return { ok: true, amount: x === left ? null : fromScaled(x), final: x === left }
}

/**
 * The default ⚡ clawback of a refund: the same share of the granted ⚡ as the share of the
 * money (rounded down to a whole ⚡, as the server does), never more than is left to take back.
 * The final refund takes everything not taken back yet. Exact (BigInt), no float.
 */
export function proportionalClawback(p: {
  amount: string
  credits_to_grant: string
  refund: string | null
  left_to_claw: string
}): string {
  const left = toScaled(p.left_to_claw) ?? 0n
  if (p.refund === null) return fromScaled(left)
  const total = toScaled(p.amount) ?? 0n
  const granted = toScaled(p.credits_to_grant) ?? 0n
  const part = toScaled(p.refund) ?? 0n
  if (total <= 0n || granted <= 0n) return '0'
  const unit = 10n ** BigInt(SCALE)
  const share = ((granted * part) / total / unit) * unit // floor to a whole ⚡
  return fromScaled(share < left ? share : left)
}
